import {
  type EventScheduleSeriesOptionDto,
  type ParseEventScheduleResponse,
  useEventParseEventSchedule,
  useEventTranscribeEventSchedule,
} from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { getUserTimezone } from "./AddEventTimesModal.utils";

interface ParseDescriptionOptions {
  defaultCapacity?: number;
  series?: EventScheduleSeriesOptionDto[];
}

export const useEventScheduleAssistant = (eventId: string) => {
  const [error, setError] = useState<string | null>(null);

  const parseMutation = useEventParseEventSchedule();
  const transcribeMutation = useEventTranscribeEventSchedule();

  const parseDescription = useCallback(
    async (description: string, options?: ParseDescriptionOptions): Promise<ParseEventScheduleResponse | null> => {
      setError(null);

      try {
        const result = await parseMutation.mutateAsync({
          body: {
            availableSeries: options?.series,
            defaultCapacity: options?.defaultCapacity ?? 0,
            description: description.trim(),
            referenceDateTime: new Date().toISOString(),
            timeZone: getUserTimezone(),
          },
          pathParams: { eventId },
        });

        return result ?? null;
      } catch {
        setError("Could not interpret that schedule. Try rephrasing or use the manual builder.");
        return null;
      }
    },
    [eventId, parseMutation],
  );

  const transcribeAudio = useCallback(
    async (audio: Blob): Promise<string | null> => {
      setError(null);

      const mimeType = audio.type.split(";")[0]?.trim() || "audio/webm";
      const extension = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
      const file = new File([audio], `schedule-dictation.${extension}`, { type: mimeType });
      const formData = new FormData();
      formData.append("FormFile", file);

      try {
        const result = await transcribeMutation.mutateAsync({
          body: formData as unknown as { FormFile?: Blob },
          pathParams: { eventId },
        });

        return result?.text?.trim() ?? null;
      } catch {
        setError("Could not transcribe audio. Try again or type your schedule manually.");
        return null;
      }
    },
    [eventId, transcribeMutation],
  );

  return {
    error,
    isParsing: parseMutation.isPending,
    isTranscribing: transcribeMutation.isPending,
    parseDescription,
    setError,
    transcribeAudio,
  };
};
